import React from 'react';
import { Loader2 } from 'lucide-react';

const VARIANTS = {
  success: {
    trackOn: 'bg-emerald-500 shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]',
    trackOff: 'bg-gray-200',
    labelOn: 'text-white',
    labelOff: 'text-gray-500',
    focus: 'focus-visible:ring-emerald-400',
  },
  amber: {
    trackOn: 'bg-amber-500 shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]',
    trackOff: 'bg-gray-200',
    labelOn: 'text-white',
    labelOff: 'text-gray-500',
    focus: 'focus-visible:ring-amber-400',
  },
};

/**
 * Compact pill switch — knob sits flush at each end; label stays in the opposite half.
 */
const PillToggle = ({
  checked = false,
  onChange,
  disabled = false,
  loading = false,
  variant = 'success',
  onLabel = 'On',
  offLabel = 'Off',
  ariaLabel,
  className = '',
}) => {
  const styles = VARIANTS[variant] || VARIANTS.success;
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel || (checked ? onLabel : offLabel)}
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDisabled && onChange) onChange(!checked);
      }}
      className={`
        relative inline-flex h-6 w-[3.75rem] shrink-0 rounded-full p-0.5
        transition-colors duration-200 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        ${styles.focus}
        ${checked ? styles.trackOn : styles.trackOff}
        ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        ${className}
      `}
    >
      <span
        className={`
          pointer-events-none absolute inset-y-0 flex items-center text-[9px] font-semibold uppercase leading-none
          transition-all duration-200 ease-out
          ${checked
            ? `left-1.5 right-[1.375rem] justify-start ${styles.labelOn}`
            : `left-[1.375rem] right-1.5 justify-end ${styles.labelOff}`}
        `}
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : (
          checked ? onLabel : offLabel
        )}
      </span>

      <span
        className={`
          pointer-events-none absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm
          transition-all duration-200 ease-out
          ${checked ? 'right-0.5 left-auto' : 'left-0.5 right-auto'}
        `}
      />
    </button>
  );
};

export default PillToggle;
